import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'next-i18next';
import styled from "styled-components";
import palette from "../styles/palette";
import { useRouter } from "next/router";

const LanguageSelectorContainer = styled.div`
  position: relative;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem;

  @media (max-width: 991px) {
    margin-left: ${({ isDropdownOpen }) => (isDropdownOpen ? "8px" : "24px")};
    margin-right: ${({ isDropdownOpen }) => (isDropdownOpen ? "8px" : "24px")};
    margin-top: ${({ isDropdownOpen }) => (isDropdownOpen ? "0" : "16px")};
    padding: 0;
  }
`;

const LanguageLabel = styled.span`
  color: ${({ open }) => (open ? "#9FE870" : "#FFFFFF")};
  font-family: "Hind Madurai", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.05em;
  text-align: left;
  transition: color 0.3s ease-in-out;
  margin-left: 5px !important;

  @media (max-width: 991px) {
    font-size: 14px !important;
    margin-left: 0 !important;
  }

  @media (min-width: 992px) {
    display: none;
    margin-left: ${({ open, isDropdownOpen }) =>
      open && isDropdownOpen ? "16px" : "0px"} !important;
  }
`;

const FlagIconMenu = styled.img`
  width: 24px;
  height: auto;
  margin-right: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const FlagIconMenuNav = styled.img`
  width: 24px;
  height: auto;
  margin-right: 8px;
  cursor: pointer;
  margin-top: 0px;

  &:hover {
    opacity: 0.8;

    ~ svg > path {
      fill: ${palette.green1};
    }
  }
`;

const DropdownArrow = styled.svg`
  width: 8px;
  height: 6px;
  margin-right: 14px;
  margin-top: ${({ isSmallScreen }) => (isSmallScreen ? "2px" : "0px")};
  cursor: pointer;
  transition: transform 0.3s ease-in-out, fill 0.3s ease-in-out;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0deg)")};
  fill: ${({ open }) => (open ? "#9FE870" : "#ffffff")};

  &:hover {
    > path {
      fill: ${palette.green1};
    }
  }
`;

const LanguageOptionsContainer = styled.div`
  position: ${({ isSmallScreen }) => (isSmallScreen ? "relative" : "absolute")};
  top: calc(100% + 2px);
  left: 0;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  background-color: #15191e;
  border-radius: 4px;
  min-width: max-content;
  z-index: 999;
  width: 100%;
  padding: 16px;
  gap: 8px;

  @media (max-width: 991px) {
  }

  @media (min-width: 992px) {
    position: absolute;
    padding: 18px !important;
    border-radius: 8px;
  }
`;

const LanguageOptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  cursor: pointer;
  border-radius: ${({ isDropdownOpen }) => (isDropdownOpen ? "8px" : "0")};

  @media (max-width: 991px) {
    display: ${({ isSmallScreen }) => (isSmallScreen ? "flex" : "none")};
    border-radius: ${({ isDropdownOpen }) => (isDropdownOpen ? "8px" : "0")} !important;
    background-color: ${({ isDropdownOpen }) =>
      isDropdownOpen ? "#1D202A" : "transparent"} !important;
    margin-top: ${({ isDropdownOpen }) =>
      isDropdownOpen ? "16px" : "0px"} !important;
    margin-bottom: ${({ isDropdownOpen }) =>
      isDropdownOpen ? "16px" : "0px"} !important;
    padding: ${({ isDropdownOpen }) =>
      isDropdownOpen ? "16px" : "0px"} !important;
  }
`;

const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  cursor: pointer;
  margin-bottom: ${({ isLast }) => (isLast ? "0" : "8px")};

  &:hover {
    color: #9fe870;
    text-decoration: underline;
  }
  @media (max-width: 991px) {
    margin-top: 8px;
    padding: 0;
  }
`;

const LanguageName = styled.span`
  color: #ffffff;
  font-family: "Hind Madurai", sans-serif;
  font-size: 0.875rem;
  font-weight: 500 !important;
  line-height: 24px;
  letter-spacing: 0.05em;
  text-align: left;
  transition: color 0.3s ease-in-out;
  white-space: nowrap;
  flex-grow: 1;
  
  &:hover {
    color: #9fe870;
    text-decoration: underline;
  }

  @media (max-width: 991px) {
    font-size: 14px !important;
  }
`;

const LanguageSelector = ({ showLabel = true }) => {
  const { i18n, t } = useTranslation();
  const router = useRouter()
  const [selectedLanguage, setSelectedLanguage] = useState(router.query.locale);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const languageSelectorRef = useRef(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    changeLanguage(router.query.locale);
  }, [router.query.locale]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageSelectorRef.current &&
        !languageSelectorRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 992);
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const languages = [
    {
      code: "pt",
      name: "Português (BR)",
      flag: "/landings/flags/flag-for-brazil.png",
    },
    {
      code: "en",
      name: "English",
      flag: "/landings/flags/flag-for-united-states.svg",
    },
  ];

  const addRouteLocale = (languageCode = '') => {
    const previousLocale = router.query.locale;
    const currentLocale = i18n.language;

    const [pathWithoutQuery, queryParams] = router.asPath.split('?');
    const currentPath = pathWithoutQuery.replace(`/${router.query.locale}`, '');
    const baseFinalPath = `${languageCode ? `/${languageCode}` : ''}${currentPath}`;
    
    if (previousLocale !== currentLocale) {
      const isBlogRoute = router.asPath.includes('blog');
      if (isBlogRoute) {
        router.replace(`/${languageCode}/blog/`);
        return;
      }
      
      // Se mudou o idioma, navega sem query params
      router.replace(baseFinalPath);
      return;
    }

    // Se não mudou o idioma, mantém os query params
    const finalPath = queryParams 
      ? `${baseFinalPath}?${queryParams}`
      : baseFinalPath;

    router.replace(finalPath);
  }

  const changeLanguage = (languageCode) => {
    setSelectedLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    addRouteLocale(languageCode);
    setIsDropdownOpen(false);
  };
  
  const getFlagIcon = () => {
    const languageFlag = languages.find((lang) => lang.code === selectedLanguage)?.flag;
    return languageFlag || "/landings/flags/flag-for-brazil.png"
  }

  return (
    <LanguageSelectorContainer
      isDropdownOpen={isDropdownOpen}
      ref={languageSelectorRef}
      className="language-selector-container"
      data-open={isDropdownOpen}
    >
      <LanguageOptionRow
        isDropdownOpen={isDropdownOpen}
        isSmallScreen={isSmallScreen}
        className="language-option-row"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        {showLabel && <LanguageLabel open={isDropdownOpen} isDropdownOpen={isDropdownOpen} className="language-label">
          {t("header.language")}
        </LanguageLabel>}
        <div style={{ display: "flex", alignItems: "center" }}>
          <FlagIconMenuNav
            src={getFlagIcon()}
            alt="Language Flag"
          />
          <DropdownArrow
            open={isDropdownOpen}
            isSmallScreen={isSmallScreen}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 8 6"
          >
            <path
              d="M0.330314 1.90516L3.21591 4.79076C3.65042 5.22527 4.35232 5.22527 4.78684 4.79076L7.67243 1.90516C8.37434 1.20326 7.87298 0 6.8814
              0H1.11021C0.118629 0 -0.371588 1.20326 0.330314 1.90516Z"
              fill={isDropdownOpen ? "#9FE870" : "currentColor"}
            />
          </DropdownArrow>
        </div>
      </LanguageOptionRow>
      <LanguageOptionsContainer
        isOpen={isDropdownOpen}
        isSmallScreen={isSmallScreen}
      >
        {languages.map((language, index) => (
          <LanguageOption
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            isLast={index === languages.length - 1}
          >
            <FlagIconMenu src={language.flag} alt="Language Flag" />
            <LanguageName name={language.name}>{language.name}</LanguageName>
          </LanguageOption>
        ))}
      </LanguageOptionsContainer>
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;
